import { Box, Button, Checkbox, Flex, Text, Tooltip } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import SvgIcon from "~/components/SvgIcon";
import {
  useApiMutation,
  useCurrentLocation,
  useOrgs,
  useQueryClient,
} from "~/hooks";
import type { CameraColumnValueTypeV2 } from "~/hooks/Camera/v2";
import { getDateFormat } from "~/utils/Helper";
import { LooseObject } from "~/utils/Types";
import { formatDateValue } from "~/utils/columns";
import { formatToMaxDecimals } from "~/utils/formatToMaxDecimals";
import { palette } from "~/utils/palette";

type DatasetTaggingTags = {
  [key: string]: string | number | boolean;
};

interface ImageDetailsTraitsProps {
  traitsInterested: MeqColumnDef<MeqRowData>[];
  overrides: string[];
  tagsResult?: DatasetTaggingTags;
  currentItem: LooseObject;
  tags?: string[];
  showPlantUserGrade?: boolean;
}

const PLANT_USER_GRADE_KEY = "PLANT_USER_GRADE";

/**
 * Only the v2 column builder puts `valueType` in a column's meta, so v1's
 * columns fall through to the numeric formatting they have always used.
 */
const formatTraitValue = <T,>(
  column: MeqColumnDef<MeqRowData>,
  value: T,
  formats: { dateFormat: string; dateTimeFormat: string },
): T | string => {
  const { valueType } = (column.meta ?? {}) as {
    valueType?: CameraColumnValueTypeV2;
  };
  if (valueType === "date" || valueType === "datetime") {
    return (
      formatDateValue(
        value,
        valueType === "date" ? formats.dateFormat : formats.dateTimeFormat,
      ) ?? ""
    );
  }
  return formatToMaxDecimals(value);
};

const ImageDetailsTraits = ({
  traitsInterested,
  overrides,
  tagsResult,
  currentItem,
  tags,
  showPlantUserGrade,
}: ImageDetailsTraitsProps) => {
  const { currentOrg, getOrgShortName } = useOrgs();
  const location = useCurrentLocation();
  const queryClient = useQueryClient();

  const formats = {
    dateFormat: getDateFormat(currentOrg, "short"),
    dateTimeFormat: getDateFormat(currentOrg, "default"),
  };

  // For each overrideable trait, keep local state for the input value
  const [overrideInputs, setOverrideInputs] = useState<{
    [key: string]: string | number | undefined;
  }>(() => {
    const initial: { [key: string]: string | number | undefined } = {};
    traitsInterested.forEach((i) => {
      const accessorKey = i.accessorKey as string;
      if (typeof accessorKey === "string" && overrides.includes(accessorKey)) {
        let val = tagsResult?.[accessorKey];
        if (typeof val === "boolean") val = val ? 1 : "";
        initial[accessorKey] = val ?? "";
      }
    });
    return initial;
  });

  // Sync overrideInputs with API data when traitsInterested, tagsResult, or currentItem changes
  useEffect(() => {
    const updated: { [key: string]: string | number | undefined } = {};
    traitsInterested.forEach((i) => {
      const accessorKey = i.accessorKey as string;
      if (typeof accessorKey === "string" && overrides.includes(accessorKey)) {
        let val = tagsResult?.[accessorKey];
        if (typeof val === "boolean") val = val ? 1 : "";
        updated[accessorKey] = val ?? "";
      }
    });
    setOverrideInputs(updated);
  }, [traitsInterested, tagsResult, currentItem, overrides]);

  const overrideMutation = useApiMutation({
    config: { method: "post" },
    path: "/user/meq/dataset-tagging/add",
  });

  // Helper to update the override value in the query cache
  const updateOverrideInCache = useCallback(
    (tagKey: string, tagValue: string | number | boolean | undefined) => {
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            return (
              typeof query.queryKey?.[0] === "string" &&
              query.queryKey[0].includes("/user/meq/dataset-tagging/tags")
            );
          },
        },
        (oldData: unknown) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          const updated = [...oldData];
          if (updated[0]) {
            updated[0] = { ...updated[0], [tagKey]: tagValue };
          }
          return updated;
        },
      );
    },
    [queryClient],
  );

  // Shared helper to mutate a tag and update the cache
  const saveTag = useCallback(
    (
      tagKey: string,
      tagValue: string | number | boolean | undefined,
      cacheValue: string | number | boolean | undefined,
    ) => {
      const isDelete = tagValue === undefined;
      overrideMutation.mutate(
        {
          data: {
            org: getOrgShortName(),
            location,
            event: {
              dataset: "camera",
              originId: {
                companyLocation: location,
                meatType: currentItem?.MEAT_TYPE,
                barcode: currentItem?.BARCODE,
                photoDateTime: currentItem?.PHOTO_DATETIME,
              },
              datasetSchemaVersion: 1,
              eventSchemaVersion: 1,
              eventAction: isDelete ? "delete" : "create",
              tagKey,
              ...(isDelete ? {} : { tagValue }),
            },
          },
        },
        {
          onSuccess: () => {
            updateOverrideInCache(tagKey, cacheValue);
          },
        },
      );
    },
    [
      overrideMutation,
      getOrgShortName,
      location,
      currentItem,
      updateOverrideInCache,
    ],
  );

  // Plant User Grade state
  const [plantUserGrade, setPlantUserGrade] = useState<string>("");
  const [plantUserGradeError, setPlantUserGradeError] = useState<string>("");

  // Sync Plant User Grade with API data
  useEffect(() => {
    if (tagsResult && tagsResult[PLANT_USER_GRADE_KEY] !== undefined) {
      setPlantUserGrade(String(tagsResult[PLANT_USER_GRADE_KEY]));
    } else {
      setPlantUserGrade("");
    }
    setPlantUserGradeError("");
  }, [tagsResult, currentItem]);

  const validatePlantUserGrade = useCallback((value: string): string => {
    if (value === "") return "";
    if (!/^\d{3,4}$/.test(value))
      return "Must be a 3-digit or 4-digit number (e.g. 100)";
    return "";
  }, []);

  const handlePlantUserGradeSave = useCallback(() => {
    const error = validatePlantUserGrade(plantUserGrade);
    setPlantUserGradeError(error);
    if (error) return;

    if (plantUserGrade === "") {
      saveTag(PLANT_USER_GRADE_KEY, undefined, undefined);
    } else {
      saveTag(
        PLANT_USER_GRADE_KEY,
        Number(plantUserGrade),
        Number(plantUserGrade),
      );
    }
  }, [plantUserGrade, validatePlantUserGrade, saveTag]);

  // Handler for input change
  const handleOverrideChange = (accessorKey: string, value: string) => {
    setOverrideInputs((prev) => ({ ...prev, [accessorKey]: value }));
  };

  // Handler for blur or Enter
  const handleOverrideSave = (accessorKey: string) => {
    const value = overrideInputs[accessorKey];
    if (value === undefined) return;
    if (value === "") {
      saveTag(accessorKey, undefined, undefined);
    } else {
      const numericValue = typeof value === "string" ? Number(value) : value;
      saveTag(accessorKey, numericValue, numericValue);
    }
  };

  return (
    <>
      <Box>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            alignItems: "center",
            backgroundColor: "var(--mantine-primary-color-filled)",
            color: "white",
            borderRadius: "4px 4px 0 0",
            margin: "-1rem -1rem 8px",
            padding: "8px 16px",
          }}
        >
          <Text size="sm" fw={600}>
            Trait
          </Text>
          <Flex align="center" justify="center" mr={16}>
            <SvgIcon name="User" color="white" />
          </Flex>
          <Flex align="center" justify="flex-end" mr={8}>
            <SvgIcon name="Camera" color="white" />
          </Flex>
        </Box>

        <Box
          style={{
            display: "grid",
            gridTemplateRows: `repeat(${traitsInterested.length}, auto)`,
            gap: 4,
          }}
        >
          {traitsInterested.map((i, idx) => {
            const accessorKey = i.accessorKey as string;
            const isStringKey = typeof accessorKey === "string";
            const isOverridable =
              isStringKey && overrides.includes(accessorKey);
            const overrideValue =
              isStringKey && overrideInputs[accessorKey] !== undefined
                ? overrideInputs[accessorKey]
                : tagsResult
                  ? typeof tagsResult[accessorKey] === "boolean"
                    ? tagsResult[accessorKey]
                      ? 1
                      : ""
                    : tagsResult[accessorKey]
                  : undefined;
            const originalValue = accessorKey ? currentItem?.[accessorKey] : "";
            if (isOverridable) {
              return (
                <Box
                  key={accessorKey || idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 8,
                    alignItems: "center",
                    borderBottom:
                      "1px solid var(--mantine-color-border-secondary)",
                    paddingBlock: 4,
                  }}
                >
                  <Text size="sm" data-trait-key={accessorKey}>
                    {i.header}
                  </Text>
                  <Box
                    component="input"
                    type="number"
                    data-trait-input={accessorKey}
                    value={
                      typeof overrideValue === "boolean"
                        ? overrideValue
                          ? 1
                          : ""
                        : (overrideValue ?? "")
                    }
                    onChange={(e) =>
                      handleOverrideChange(
                        accessorKey,
                        (e.target as HTMLInputElement).value,
                      )
                    }
                    onBlur={() => handleOverrideSave(accessorKey)}
                    onKeyDown={(e) => {
                      if (
                        (e as React.KeyboardEvent<HTMLInputElement>).key ===
                        "Enter"
                      ) {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    style={{
                      width: "100%",
                      border: "1px solid var(--mantine-color-border-secondary)",
                      borderRadius: 4,
                      padding: 4,
                      fontSize: 14,
                    }}
                  />
                  <Text ta="right" size="sm" data-trait-value={accessorKey}>
                    {formatTraitValue(i, originalValue, formats)}
                  </Text>
                </Box>
              );
            } else {
              const isWagyuMarbling =
                showPlantUserGrade &&
                isStringKey &&
                accessorKey.includes("JBS_WAGYU_MARBLING");
              return (
                <Box
                  key={accessorKey || idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 8,
                    alignItems: "center",
                    borderBottom:
                      "1px solid var(--mantine-color-border-secondary)",
                    paddingBlock: 4,
                  }}
                >
                  <Text size="sm" data-trait-key={accessorKey}>
                    {i.header}
                  </Text>
                  {isWagyuMarbling ? (
                    <>
                      <Tooltip
                        label={plantUserGradeError}
                        opened={!!plantUserGradeError}
                        color="red"
                        position="top"
                        withArrow
                        zIndex={1500}
                      >
                        <Box
                          component="input"
                          type="number"
                          value={plantUserGrade}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const val = (e.target as HTMLInputElement).value;
                            if (val.length > 4) return;
                            setPlantUserGrade(val);
                            setPlantUserGradeError("");
                          }}
                          onBlur={() => handlePlantUserGradeSave()}
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLInputElement>,
                          ) => {
                            if (e.key === "Enter") {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          style={{
                            width: "100%",
                            border: "1px solid",
                            borderColor: plantUserGradeError
                              ? "var(--mantine-color-error)"
                              : "var(--mantine-color-border-secondary)",
                            borderRadius: 4,
                            padding: 4,
                            fontSize: 14,
                          }}
                          aria-label="Plant User Grade"
                        />
                      </Tooltip>
                      <Text ta="right" size="sm" data-trait-value={accessorKey}>
                        {formatTraitValue(i, originalValue, formats)}
                      </Text>
                    </>
                  ) : (
                    <Box ta="right" style={{ gridColumn: "2 / span 2" }}>
                      <Text size="sm" data-trait-value={accessorKey}>
                        {formatTraitValue(i, originalValue, formats)}
                      </Text>
                    </Box>
                  )}
                </Box>
              );
            }
          })}
        </Box>
      </Box>

      {tags && tags.length > 0 && (
        <Flex direction="column" gap={8} mt={16}>
          <Text size="sm" fw={600}>
            Tags
          </Text>

          {tags.map((tag) => {
            const checked = !!(
              tagsResult &&
              typeof tagsResult[tag] === "boolean" &&
              tagsResult[tag]
            );
            return (
              <Box key={tag} w="100%" ta="left">
                <Button
                  color="dark"
                  leftSection={
                    <Checkbox.Indicator checked={checked} size="xs" />
                  }
                  size="compact-xs"
                  style={{
                    borderColor: palette.border.secondary,
                    color: palette.text.primary,
                  }}
                  variant="outline"
                  onClick={() => {
                    const newValue = !checked;
                    if (!newValue) {
                      saveTag(tag, undefined, false);
                    } else {
                      saveTag(tag, true, true);
                    }
                  }}
                >
                  {tag}
                </Button>
              </Box>
            );
          })}
        </Flex>
      )}
    </>
  );
};

export default ImageDetailsTraits;
